import React, { useState } from "react"
import { Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faMinus, faPlus, faTrashAlt } from "@fortawesome/free-solid-svg-icons"
import { motion } from "framer-motion"
import { theme } from "../settings/config"
import DashedOutline from "../atoms/DashedOutline"
import SchemaDrawing from "../lesson/schema/SchemaDrawing"
import { SCHEMA_SURFACES, schemaSurfaceClass } from "../lesson/schema/schemaSurface"

// Il colore della superficie è quello che si usa nella lista lezioni, e lo
// stesso degli schemi: se il tipo non è tra quelli del tema il campo resta
// neutro invece di sparire.
const surfaceColor = surfaceType => theme.colors[surfaceType] ?? theme.colors.gunmetal_light

// Il campo piccolo dipinto come uno schema di quella superficie: la sua
// classe, o le variabili dello schema al colore neutro
const SCHEMA_IDS = SCHEMA_SURFACES.map(surface => surface.id)
const courtLook = surfaceType =>
	SCHEMA_IDS.includes(surfaceType)
		? { className: schemaSurfaceClass(surfaceType), style: {} }
		: {
				className: undefined,
				style: {
					"--c-schema-court": surfaceColor(surfaceType),
					"--c-schema-inside": surfaceColor(surfaceType),
				},
			}

// I campi sono numerati, e il numero finisce dentro le lezioni salvate:
// quello nuovo continua la serie invece di riempire i buchi, così un campo
// tolto e rimesso non si prende le lezioni di quello vecchio. Se il numero
// giusto è un altro, lo si cambia dopo.
const nextCourtNumber = sportCourts => {
	const numbers = Object.keys(sportCourts ?? {}).map(Number)
	return String(numbers.length === 0 ? 1 : Math.max(...numbers) + 1)
}

// La funzione che salva il circolo vuole numeri da una a tre cifre; lo 0 è
// la lezione senza campo
const MAX_COURT_NUMBER = 999

// Il numero libero più vicino in quella direzione: i numeri dei campi del
// circolo possono saltare (1, 2, 3, 5), e quelli già presi da un altro
// campo dello stesso sport si saltano. Gli altri sport hanno la loro
// numerazione: un campo 4 da tennis e un campo 4 da padel stanno insieme.
export const freeCourtNumber = (sportCourts, from, direction) => {
	let number = Number(from) + direction
	while (sportCourts[String(number)]) number += direction
	return number >= 1 && number <= MAX_COURT_NUMBER ? String(number) : null
}

const ring = (isSelected, gap) =>
	isSelected ? `0 0 0 2px ${gap}, 0 0 0 4px ${theme.colors.primary}` : "none"

// Un campo piccolo, in prospettiva e pieno del colore della sua superficie:
// quelli del circolo e quelli da cui si sceglie la superficie sono lo
// stesso riquadro. `gap` è il fondo su cui sta, che separa l'anello
// arancione dal colore del campo.
function CourtTile({ surfaceType, ariaLabel, isSelected, gap, disabled, onClick, children }) {
	const look = courtLook(surfaceType)
	return (
		<motion.button
			type="button"
			className={look.className}
			disabled={disabled}
			aria-pressed={disabled ? undefined : isSelected}
			aria-label={ariaLabel}
			whileTap={disabled ? undefined : { scale: 0.95 }}
			onClick={onClick}
			style={{
				...look.style,
				position: "relative",
				display: "flex",
				flexDirection: "column",
				alignItems: "stretch",
				minWidth: 0,
				padding: "0.125rem 0.375rem 0.5rem",
				border: 0,
				borderRadius: "15px",
				background: theme.colors.schema_court,
				boxShadow: ring(isSelected, gap),
				color: theme.colors.white,
				textShadow: `0 1px 2px ${theme.colors.schema_shade}`,
				font: "inherit",
				cursor: disabled ? "default" : "pointer",
				transition: "box-shadow 0.15s",
			}}>
			<span aria-hidden="true" style={{ display: "block", width: "100%", aspectRatio: "358 / 400" }}>
				<SchemaDrawing schema={null} label="" grass={false} />
			</span>
			<span
				style={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "center",
					gap: "0.375rem",
					minWidth: 0,
				}}>
				{children}
			</span>
			{isSelected && (
				<span
					aria-hidden="true"
					style={{
						position: "absolute",
						top: "0.375rem",
						right: "0.375rem",
						width: "1.25rem",
						height: "1.25rem",
						borderRadius: "50%",
						background: theme.colors.primary,
						color: theme.colors.white,
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "0.625rem",
						boxShadow: `0 1px 3px ${theme.colors.schema_shadow}`,
					}}>
					<FontAwesomeIcon icon={faCheck} />
				</span>
			)}
		</motion.button>
	)
}

const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }

// I tondi del − e del +, sul fondo caldo come i blocchi dentro una scheda
function StepButton({ icon, label, disabled, onClick }) {
	return (
		<motion.button
			type="button"
			aria-label={label}
			disabled={disabled}
			whileTap={disabled ? undefined : { scale: 0.9 }}
			onClick={onClick}
			style={{
				width: "2.25rem",
				height: "2.25rem",
				borderRadius: "50%",
				border: 0,
				background: theme.colors.background,
				color: theme.colors.gunmetal,
				opacity: disabled ? 0.4 : 1,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontSize: "0.75rem",
				cursor: disabled ? "default" : "pointer",
			}}>
			<FontAwesomeIcon icon={icon} />
		</motion.button>
	)
}

const blockTitle = { fontSize: "0.9375rem", fontWeight: 600, color: theme.colors.gunmetal }
const blockHint = { fontSize: "0.75rem", color: theme.colors.text_light }

// I campi si guardano, non si leggono: a colpo d'occhio si vede quanti sono
// e di che superficie. Ognuno è un campo piccolo, in prospettiva e pieno del
// suo colore, come quelli della superficie degli schemi nel profilo: il
// disegno è lo stesso, e così la classe della superficie, che sul cemento
// mette il blu dentro le righe e il verde intorno. Un tipo che gli schemi
// non conoscono resta neutro.
//
// Toccato un campo, sotto la griglia si apre la sua scheda: il numero, la
// superficie (gli stessi campi piccoli) e il tasto per toglierlo. Una volta
// sola, invece di ripetere le opzioni sotto ogni campo.
function SchoolCourtsEditor({ sportID, courts, surfaces, isEditing, onChange }) {
	const sportCourts = courts[sportID] ?? {}
	const numbers = Object.keys(sportCourts).sort((a, b) => Number(a) - Number(b))

	const [selected, setSelected] = useState(null)
	const selectedCourt = selected && sportCourts[selected] ? selected : null

	// Il numero che un campo aveva quando si è aperto il foglio, per quelli a
	// cui lo si è cambiato: le lezioni già segnate restano su quello, e lo si
	// dice. Un campo nuovo ha null: non ha lezioni da lasciare indietro.
	const [initialCourts] = useState(courts)
	const [savedNumbers, setSavedNumbers] = useState({})
	const key = number => `${sportID}/${number}`
	const savedNumberOf = number =>
		key(number) in savedNumbers
			? savedNumbers[key(number)]
			: initialCourts[sportID]?.[number]
				? number
				: null

	const setSportCourts = next => onChange({ ...courts, [sportID]: next })

	const setCourt = (number, surface) =>
		setSportCourts({
			...sportCourts,
			[number]: { surfaceType: surface.surfaceType, surfaceName: surface.surfaceName },
		})

	const addCourt = () => {
		const firstSurface = Object.values(surfaces)[0]
		if (!firstSurface) return
		const number = nextCourtNumber(sportCourts)
		setCourt(number, firstSurface)
		setSavedNumbers({ ...savedNumbers, [key(number)]: null })
		setSelected(number)
	}

	const renumberCourt = (from, to) => {
		if (!to) return
		const next = { ...sportCourts, [to]: sportCourts[from] }
		delete next[from]

		const saved = { ...savedNumbers, [key(to)]: savedNumberOf(from) }
		delete saved[key(from)]
		setSavedNumbers(saved)

		setSelected(to)
		setSportCourts(next)
	}

	const removeCourt = number => {
		const remaining = { ...sportCourts }
		delete remaining[number]

		// tolto l'ultimo campo, lo sport non ha più senso di esistere
		const next = { ...courts }
		if (Object.keys(remaining).length === 0) delete next[sportID]
		else next[sportID] = remaining

		setSelected(null)
		onChange(next)
	}

	if (!sportID) {
		return (
			<Text textSize="body" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
				Nessun campo. {isEditing ? "Scegli uno sport qui sopra per cominciare." : ""}
			</Text>
		)
	}

	const lower = selectedCourt && freeCourtNumber(sportCourts, selectedCourt, -1)
	const higher = selectedCourt && freeCourtNumber(sportCourts, selectedCourt, 1)
	const savedNumber = selectedCourt && savedNumberOf(selectedCourt)
	const keepsLessons = savedNumber && savedNumber !== selectedCourt

	return (
		<>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
					gap: "0.625rem",
					// l'anello del campo scelto sta fuori dal riquadro: il foglio
					// scorre da qui, e senza questo spazio lo taglierebbe in alto
					paddingTop: "0.25rem",
					marginBottom: "1rem",
				}}>
				{numbers.map(number => {
					const court = sportCourts[number]
					const isSelected = isEditing && selectedCourt === number
					return (
						<CourtTile
							key={number}
							surfaceType={court?.surfaceType}
							ariaLabel={`Campo ${number}, ${court?.surfaceName ?? ""}`}
							isSelected={isSelected}
							gap={theme.colors.background}
							disabled={!isEditing}
							onClick={() => setSelected(isSelected ? null : number)}>
							<span style={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1 }}>{number}</span>
							<span style={{ fontSize: "0.75rem", fontWeight: 500, ...ellipsis }}>
								{court?.surfaceName}
							</span>
						</CourtTile>
					)
				})}

				{isEditing && (
					<motion.button
						type="button"
						aria-label="Aggiungi un campo"
						whileTap={{ scale: 0.95 }}
						onClick={addCourt}
						style={{
							position: "relative",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							minHeight: "6rem",
							border: 0,
							borderRadius: "15px",
							background: "transparent",
							cursor: "pointer",
						}}>
						<DashedOutline color={theme.colors.gray_light} width={3} radius={15} dash="10 8" />
						<FontAwesomeIcon icon={faPlus} color={theme.colors.text_light} />
					</motion.button>
				)}
			</div>

			{isEditing && selectedCourt && (
				<motion.div
					key="court"
					initial={{ opacity: 0, y: -6 }}
					animate={{ opacity: 1, y: 0 }}
					style={{
						background: theme.colors.surface,
						borderRadius: "15px",
						padding: "0.875rem 0.875rem 0.375rem",
						marginBottom: "1rem",
						display: "flex",
						flexDirection: "column",
						gap: "0.875rem",
					}}>
					<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
						<div style={{ flexGrow: 1, minWidth: 0 }}>
							<div style={blockTitle}>Numero</div>
							<div style={blockHint}>
								{keepsLessons
									? `Le lezioni già segnate restano sul campo ${savedNumber}`
									: "Quello del circolo, anche se salta"}
							</div>
						</div>
						<div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
							<StepButton
								icon={faMinus}
								label="Numero prima"
								disabled={!lower}
								onClick={() => renumberCourt(selectedCourt, lower)}
							/>
							<span
								aria-live="polite"
								style={{
									minWidth: "2.25rem",
									textAlign: "center",
									fontSize: "1.375rem",
									fontWeight: 700,
									color: theme.colors.gunmetal,
								}}>
								{selectedCourt}
							</span>
							<StepButton
								icon={faPlus}
								label="Numero dopo"
								disabled={!higher}
								onClick={() => renumberCourt(selectedCourt, higher)}
							/>
						</div>
					</div>

					<div>
						<div style={{ ...blockTitle, marginBottom: "0.5rem" }}>Superficie</div>
						<div
							role="group"
							aria-label={`Superficie del campo ${selectedCourt}`}
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
								gap: "0.5rem",
								paddingTop: "0.25rem",
							}}>
							{Object.keys(surfaces).map(surfaceID => {
								const surface = surfaces[surfaceID]
								return (
									<CourtTile
										key={surfaceID}
										surfaceType={surface.surfaceType}
										ariaLabel={surface.surfaceName}
										isSelected={sportCourts[selectedCourt]?.surfaceType === surface.surfaceType}
										gap={theme.colors.surface}
										onClick={() => setCourt(selectedCourt, surface)}>
										<span style={{ fontSize: "0.75rem", fontWeight: 600, ...ellipsis }}>
											{surface.surfaceName}
										</span>
									</CourtTile>
								)
							})}
						</div>
					</div>

					{/* dentro un modulo non si chiede conferma: finché non si salva
					    non è successo niente (docs/interfaccia.md) */}
					<div style={{ borderTop: `1px solid ${theme.colors.background}`, paddingTop: "0.125rem" }}>
						<button
							type="button"
							onClick={() => removeCourt(selectedCourt)}
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "0.5rem",
								border: 0,
								background: "transparent",
								padding: "0.625rem 0",
								font: "inherit",
								fontSize: "0.9375rem",
								fontWeight: 600,
								color: theme.colors.red,
								cursor: "pointer",
							}}>
							<FontAwesomeIcon icon={faTrashAlt} />
							Togli il campo {selectedCourt}
						</button>
					</div>
				</motion.div>
			)}

			{isEditing && !selectedCourt && numbers.length > 0 && (
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
					Tocca un campo per cambiarne il numero, la superficie o toglierlo.
				</Text>
			)}
		</>
	)
}

export default SchoolCourtsEditor
